import React, { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  name?: string;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

type WindowWithReporters = Window & {
  Sentry?: { captureException: (err: unknown, ctx?: Record<string, unknown>) => void };
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true, errorId: crypto.randomUUID() };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const boundary = this.props.name || 'root';
    console.error(`[ErrorBoundary:${boundary}]`, error, info);

    if (typeof window !== 'undefined') {
      const reporter = (window as WindowWithReporters).Sentry;
      reporter?.captureException(error, {
        tags: { boundary },
        extra: { componentStack: info.componentStack, errorId: this.state.errorId },
      });
    }

    this.props.onError?.(error, info);
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorId: null });
  };

  handleReload = () => {
    this.setState({ hasError: false, errorId: null });
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex min-h-[50vh] items-center justify-center p-6">
            <div className="max-w-md text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-line bg-[var(--state-danger-bg)]">
                <svg
                  className="h-6 w-6 text-state-danger"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 className="mb-2 text-xl font-medium text-ink">Something went wrong</h2>
              <p className="mb-4 text-ink/85">An unexpected error occurred.</p>
              {this.state.errorId && (
                <p className="mb-4 text-xs text-muted">Error ID: {this.state.errorId}</p>
              )}
              <div className="flex justify-center gap-2">
                <button
                  onClick={this.handleRetry}
                  className="rounded-full border border-line px-4 py-2 text-ink/85 transition-colors hover:border-line-strong hover:bg-paper-2"
                >
                  Try again
                </button>
                <button
                  onClick={this.handleReload}
                  className="rounded-full bg-ink px-4 py-2 text-paper transition-colors hover:bg-ink"
                >
                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
